import { useEffect, useRef, useCallback } from 'react'
import {
  nodeVertexShader,
  nodeFragmentShader,
  lineVertexShader,
  lineFragmentShader,
  packetVertexShader,
  packetFragmentShader,
  bgVertexShader,
  bgFragmentShader,
} from '../shaders/particles'

interface Node {
  x: number
  y: number
  size: number
  opacity: number
  type: number
  vx: number
  vy: number
  baseX: number
  baseY: number
}

interface DataPacket {
  fromIdx: number
  toIdx: number
  progress: number
  speed: number
  size: number
}

interface Connection {
  i: number
  j: number
  dist: number
}

function createShader(gl: WebGLRenderingContext, type: number, source: string) {
  const shader = gl.createShader(type)
  if (!shader) return null
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader)
    return null
  }
  return shader
}

function createProgram(gl: WebGLRenderingContext, vs: string, fs: string) {
  const v = createShader(gl, gl.VERTEX_SHADER, vs)
  const f = createShader(gl, gl.FRAGMENT_SHADER, fs)
  if (!v || !f) return null
  const p = gl.createProgram()
  if (!p) return null
  gl.attachShader(p, v)
  gl.attachShader(p, f)
  gl.linkProgram(p)
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    gl.deleteProgram(p)
    return null
  }
  return p
}

const BASE_NODE_COUNT = 140
const BASE_CONNECTION_DIST = 0.38
const MAX_PACKETS = 60

function getResponsiveConfig() {
  const w = window.innerWidth
  if (w < 640) return { nodeCount: 70, connectionDist: 0.32, maxPackets: 30 }
  if (w < 1024) return { nodeCount: 100, connectionDist: 0.35, maxPackets: 45 }
  return {
    nodeCount: BASE_NODE_COUNT,
    connectionDist: BASE_CONNECTION_DIST,
    maxPackets: MAX_PACKETS,
  }
}

export default function WebGLBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animRef = useRef(0)
  const nodesRef = useRef<Node[]>([])
  const packetsRef = useRef<DataPacket[]>([])
  const mouseRef = useRef({ x: 99, y: 99 })
  const aspectRef = useRef(1)
  const configRef = useRef(getResponsiveConfig())

  // dt control (prevents spikes after resize/layout)
  const lastFrameRef = useRef<number | null>(null)

  const initNodes = useCallback((aspect: number) => {
    const config = getResponsiveConfig()
    configRef.current = config
    const nodes: Node[] = []
    for (let i = 0; i < config.nodeCount; i++) {
      const x = (Math.random() * 2 - 1) * aspect
      const y = Math.random() * 2 - 1
      const isHub = Math.random() < 0.15
      nodes.push({
        x,
        y,
        size: isHub ? Math.random() * 6 + 6 : Math.random() * 4 + 2.5,
        opacity: isHub ? Math.random() * 0.3 + 0.6 : Math.random() * 0.3 + 0.35,
        type: isHub ? 1 : 0,
        vx: (Math.random() - 0.5) * 0.0012,
        vy: (Math.random() - 0.5) * 0.0012,
        baseX: x,
        baseY: y,
      })
    }
    nodesRef.current = nodes
    packetsRef.current = []
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const gl = canvas.getContext('webgl', {
      alpha: true,
      antialias: true,
      premultipliedAlpha: false,
    })
    if (!gl) return

    const nodeProg = createProgram(gl, nodeVertexShader, nodeFragmentShader)
    const lineProg = createProgram(gl, lineVertexShader, lineFragmentShader)
    const packetProg = createProgram(gl, packetVertexShader, packetFragmentShader)
    const bgProg = createProgram(gl, bgVertexShader, bgFragmentShader)

    if (!nodeProg || !lineProg || !packetProg || !bgProg) return

    const nPosLoc = gl.getAttribLocation(nodeProg, 'a_position')
    const nSizeLoc = gl.getAttribLocation(nodeProg, 'a_size')
    const nOpacLoc = gl.getAttribLocation(nodeProg, 'a_opacity')
    const nTypeLoc = gl.getAttribLocation(nodeProg, 'a_type')
    const nTimeLoc = gl.getUniformLocation(nodeProg, 'u_time')
    const nResLoc = gl.getUniformLocation(nodeProg, 'u_resolution')
    const nColLoc = gl.getUniformLocation(nodeProg, 'u_color')
    const nCol2Loc = gl.getUniformLocation(nodeProg, 'u_color2')
    const nAspectLoc = gl.getUniformLocation(nodeProg, 'u_aspect')

    const lPosLoc = gl.getAttribLocation(lineProg, 'a_position')
    const lAlphaLoc = gl.getAttribLocation(lineProg, 'a_alpha')
    const lColLoc = gl.getUniformLocation(lineProg, 'u_color')
    const lAspectLoc = gl.getUniformLocation(lineProg, 'u_aspect')

    const pkPosLoc = gl.getAttribLocation(packetProg, 'a_position')
    const pkSizeLoc = gl.getAttribLocation(packetProg, 'a_size')
    const pkOpacLoc = gl.getAttribLocation(packetProg, 'a_opacity')
    const pkResLoc = gl.getUniformLocation(packetProg, 'u_resolution')
    const pkColLoc = gl.getUniformLocation(packetProg, 'u_color')
    const pkAspectLoc = gl.getUniformLocation(packetProg, 'u_aspect')

    const bgPosLoc = gl.getAttribLocation(bgProg, 'a_position')
    const bgResLoc = gl.getUniformLocation(bgProg, 'u_resolution')
    const bgTimeLoc = gl.getUniformLocation(bgProg, 'u_time')

    const buffers = {
      nPos: gl.createBuffer(),
      nSize: gl.createBuffer(),
      nOpac: gl.createBuffer(),
      nType: gl.createBuffer(),
      line: gl.createBuffer(),
      lineAlpha: gl.createBuffer(),
      pkPos: gl.createBuffer(),
      pkSize: gl.createBuffer(),
      pkOpac: gl.createBuffer(),
      quad: gl.createBuffer(),
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, buffers.quad)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW)

    const remapForAspectChange = (oldAspect: number, newAspect: number) => {
      if (!oldAspect || !newAspect || oldAspect === newAspect) return
      const ratio = newAspect / oldAspect
      const nodes = nodesRef.current
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i]
        n.x *= ratio
        n.baseX *= ratio
        n.vx *= ratio
      }
      packetsRef.current = []
    }

    const handleResize = () => {
      const oldAspect = aspectRef.current

      const dpr = Math.min(window.devicePixelRatio, 2)
      canvas.width = canvas.clientWidth * dpr
      canvas.height = canvas.clientHeight * dpr
      gl.viewport(0, 0, canvas.width, canvas.height)

      const newAspect = canvas.width / canvas.height
      aspectRef.current = newAspect

      const prevConfig = configRef.current
      const nextConfig = getResponsiveConfig()
      configRef.current = nextConfig

      // ✅ FIX: always init on first load (when nodes list is empty)
      if (nodesRef.current.length === 0) {
        initNodes(newAspect)
      } else if (prevConfig.nodeCount !== nextConfig.nodeCount) {
        initNodes(newAspect)
      } else {
        remapForAspectChange(oldAspect, newAspect)
      }

      // avoid dt spike right after resize/layout
      lastFrameRef.current = null

      // keep mouse “neutral” until next move (prevents sudden push)
      mouseRef.current = { x: 99, y: 99 }
    }

    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect()
      const aspect = aspectRef.current
      mouseRef.current = {
        x: ((e.clientX - rect.left) / rect.width) * 2 * aspect - aspect,
        y: -(((e.clientY - rect.top) / rect.height) * 2 - 1),
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    window.addEventListener('mousemove', handleMouseMove)

    const startTime = performance.now()
    const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v))

    const render = () => {
      const now = performance.now()
      const time = now - startTime

      let dtNorm = 1
      if (lastFrameRef.current != null) {
        const dtMs = now - lastFrameRef.current
        dtNorm = clamp(dtMs / 16.6667, 0.5, 2.0)
      }
      lastFrameRef.current = now

      const nodes = nodesRef.current
      const packets = packetsRef.current
      const aspect = aspectRef.current
      const config = configRef.current
      const boundX = aspect + 0.15

      const mouseRadius = 0.35
      const mousePush = 0.00008
      const drift = 0.0001
      const maxV = 0.03

      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i]
        const dx = mouseRef.current.x - n.x
        const dy = mouseRef.current.y - n.y
        const dist = Math.sqrt(dx * dx + dy * dy)

        if (dist < mouseRadius) {
          n.vx -= dx * mousePush * dtNorm
          n.vy -= dy * mousePush * dtNorm
        }

        n.vx += (n.baseX - n.x) * drift * dtNorm
        n.vy += (n.baseY - n.y) * drift * dtNorm

        n.x += (n.vx + Math.sin(time * 0.0003 + i * 1.7) * 0.0002) * dtNorm
        n.y += (n.vy + Math.cos(time * 0.00025 + i * 2.3) * 0.0002) * dtNorm

        const damp = Math.pow(0.997, dtNorm)
        n.vx *= damp
        n.vy *= damp

        n.vx = clamp(n.vx, -maxV, maxV)
        n.vy = clamp(n.vy, -maxV, maxV)

        if (n.x < -boundX) n.x = boundX
        if (n.x > boundX) n.x = -boundX
        if (n.y < -1.15) n.y = 1.15
        if (n.y > 1.15) n.y = -1.15
      }

      const connections: Connection[] = []
      const linePositions: number[] = []
      const lineAlphas: number[] = []

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x
          const dy = nodes[i].y - nodes[j].y
          const d = Math.sqrt(dx * dx + dy * dy)
          if (d < config.connectionDist) {
            connections.push({ i, j, dist: d })
            const alpha = 1.0 - d / config.connectionDist
            linePositions.push(nodes[i].x, nodes[i].y, nodes[j].x, nodes[j].y)
            lineAlphas.push(alpha, alpha)
          }
        }
      }

      if (connections.length > 0 && packets.length < config.maxPackets && Math.random() < 0.15) {
        const conn = connections[Math.floor(Math.random() * connections.length)]
        const reverse = Math.random() < 0.5
        packets.push({
          fromIdx: reverse ? conn.j : conn.i,
          toIdx: reverse ? conn.i : conn.j,
          progress: 0,
          speed: Math.random() * 0.015 + 0.008,
          size: Math.random() * 3 + 2,
        })
      }

      const pkPositions: number[] = []
      const pkSizes: number[] = []
      const pkOpacities: number[] = []
      const alivePackets: DataPacket[] = []

      for (let i = 0; i < packets.length; i++) {
        const pk = packets[i]
        pk.progress += pk.speed * dtNorm
        if (pk.progress >= 1) continue

        const from = nodes[pk.fromIdx]
        const to = nodes[pk.toIdx]
        const px = from.x + (to.x - from.x) * pk.progress
        const py = from.y + (to.y - from.y) * pk.progress
        const fade =
          pk.progress < 0.15
            ? pk.progress / 0.15
            : pk.progress > 0.85
              ? (1 - pk.progress) / 0.15
              : 1.0

        pkPositions.push(px, py)
        pkSizes.push(pk.size)
        pkOpacities.push(fade * 0.9)
        alivePackets.push(pk)
      }
      packetsRef.current = alivePackets

      gl.clearColor(0, 0, 0, 0)
      gl.clear(gl.COLOR_BUFFER_BIT)
      gl.enable(gl.BLEND)

      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
      gl.useProgram(bgProg)
      gl.bindBuffer(gl.ARRAY_BUFFER, buffers.quad)
      gl.enableVertexAttribArray(bgPosLoc)
      gl.vertexAttribPointer(bgPosLoc, 2, gl.FLOAT, false, 0, 0)
      gl.uniform2f(bgResLoc, canvas.width, canvas.height)
      gl.uniform1f(bgTimeLoc, time)
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)

      gl.blendFunc(gl.SRC_ALPHA, gl.ONE)

      if (linePositions.length > 0) {
        gl.useProgram(lineProg)
        gl.bindBuffer(gl.ARRAY_BUFFER, buffers.line)
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(linePositions), gl.DYNAMIC_DRAW)
        gl.enableVertexAttribArray(lPosLoc)
        gl.vertexAttribPointer(lPosLoc, 2, gl.FLOAT, false, 0, 0)

        gl.bindBuffer(gl.ARRAY_BUFFER, buffers.lineAlpha)
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(lineAlphas), gl.DYNAMIC_DRAW)
        gl.enableVertexAttribArray(lAlphaLoc)
        gl.vertexAttribPointer(lAlphaLoc, 1, gl.FLOAT, false, 0, 0)

        gl.uniform3f(lColLoc, 0.15, 0.5, 1.0)
        gl.uniform1f(lAspectLoc, aspect)
        gl.drawArrays(gl.LINES, 0, linePositions.length / 2)
      }

      if (pkPositions.length > 0) {
        gl.useProgram(packetProg)
        gl.bindBuffer(gl.ARRAY_BUFFER, buffers.pkPos)
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(pkPositions), gl.DYNAMIC_DRAW)
        gl.enableVertexAttribArray(pkPosLoc)
        gl.vertexAttribPointer(pkPosLoc, 2, gl.FLOAT, false, 0, 0)

        gl.bindBuffer(gl.ARRAY_BUFFER, buffers.pkSize)
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(pkSizes), gl.DYNAMIC_DRAW)
        gl.enableVertexAttribArray(pkSizeLoc)
        gl.vertexAttribPointer(pkSizeLoc, 1, gl.FLOAT, false, 0, 0)

        gl.bindBuffer(gl.ARRAY_BUFFER, buffers.pkOpac)
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(pkOpacities), gl.DYNAMIC_DRAW)
        gl.enableVertexAttribArray(pkOpacLoc)
        gl.vertexAttribPointer(pkOpacLoc, 1, gl.FLOAT, false, 0, 0)

        gl.uniform2f(pkResLoc, canvas.width, canvas.height)
        gl.uniform3f(pkColLoc, 0.4, 0.85, 1.0)
        gl.uniform1f(pkAspectLoc, aspect)
        gl.drawArrays(gl.POINTS, 0, pkPositions.length / 2)
      }

      const positions = new Float32Array(nodes.length * 2)
      const sizes = new Float32Array(nodes.length)
      const opacities = new Float32Array(nodes.length)
      const types = new Float32Array(nodes.length)

      for (let i = 0; i < nodes.length; i++) {
        positions[i * 2] = nodes[i].x
        positions[i * 2 + 1] = nodes[i].y
        sizes[i] = nodes[i].size
        opacities[i] = nodes[i].opacity
        types[i] = nodes[i].type
      }

      gl.useProgram(nodeProg)

      gl.bindBuffer(gl.ARRAY_BUFFER, buffers.nPos)
      gl.bufferData(gl.ARRAY_BUFFER, positions, gl.DYNAMIC_DRAW)
      gl.enableVertexAttribArray(nPosLoc)
      gl.vertexAttribPointer(nPosLoc, 2, gl.FLOAT, false, 0, 0)

      gl.bindBuffer(gl.ARRAY_BUFFER, buffers.nSize)
      gl.bufferData(gl.ARRAY_BUFFER, sizes, gl.DYNAMIC_DRAW)
      gl.enableVertexAttribArray(nSizeLoc)
      gl.vertexAttribPointer(nSizeLoc, 1, gl.FLOAT, false, 0, 0)

      gl.bindBuffer(gl.ARRAY_BUFFER, buffers.nOpac)
      gl.bufferData(gl.ARRAY_BUFFER, opacities, gl.DYNAMIC_DRAW)
      gl.enableVertexAttribArray(nOpacLoc)
      gl.vertexAttribPointer(nOpacLoc, 1, gl.FLOAT, false, 0, 0)

      gl.bindBuffer(gl.ARRAY_BUFFER, buffers.nType)
      gl.bufferData(gl.ARRAY_BUFFER, types, gl.DYNAMIC_DRAW)
      gl.enableVertexAttribArray(nTypeLoc)
      gl.vertexAttribPointer(nTypeLoc, 1, gl.FLOAT, false, 0, 0)

      gl.uniform1f(nTimeLoc, time)
      gl.uniform2f(nResLoc, canvas.width, canvas.height)
      gl.uniform3f(nColLoc, 0.2, 0.55, 1.0)
      gl.uniform3f(nCol2Loc, 0.1, 0.8, 0.95)
      gl.uniform1f(nAspectLoc, aspect)

      gl.drawArrays(gl.POINTS, 0, nodes.length)

      animRef.current = requestAnimationFrame(render)
    }

    animRef.current = requestAnimationFrame(render)

    return () => {
      cancelAnimationFrame(animRef.current)
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [initNodes])

  return <canvas ref={canvasRef} className="fixed inset-0 w-full h-full z-0" aria-hidden="true" />
}
